// src/components/Experiment/PlateSchemaStage.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Modal, Alert, Badge, InputGroup, Carousel, ButtonGroup } from 'react-bootstrap';
import PlateGrid from './PlateGrid';
import PlateToolsContainer from './PlateToolsContainer';
import SampleTable from './SampleTable';
import ClassificationManager from './ClassificationManager';
import useImageWithAuth from '../../hooks/useImageWithAuth';
import './PlateSchemaStage.css';

const PlateSchemaStage = ({ plateSchema, selectedJobs, onSchemaChange }) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]);
  const [tempSampleName, setTempSampleName] = useState('');
  const [showSampleInput, setShowSampleInput] = useState(false);
  const [highlightedSample, setHighlightedSample] = useState(null);
  const [error, setError] = useState(null);
  const [selectedClassGroup, setSelectedClassGroup] = useState('');

  // Grid manipulation state
  const [showGridDialog, setShowGridDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [gridRows, setGridRows] = useState(plateSchema.rows);
  const [gridCols, setGridCols] = useState(plateSchema.cols);

  // Schema management state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedSchema, setUploadedSchema] = useState(null);
  const [uploadValidation, setUploadValidation] = useState({ isValid: false, errors: [] });
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Image carousel state
  const [imageIndex, setImageIndex] = useState(0);

  // Ref for the input field to handle focus
  const sampleInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Generate next sample number
  const getNextSampleNumber = useCallback(() => {
    // Find the highest existing sample number
    const existingNumbers = plateSchema.samples.map(sample => {
      const match = sample.name.match(/sample[_-]?(\d+)/i);
      return match ? parseInt(match[1]) : 0;
    });

    const highestNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return highestNumber + 1;
  }, [plateSchema.samples]);

  // Set default sample name when cells are selected
  useEffect(() => {
    if (selectedCells.length > 0 && showSampleInput) {
      const nextNumber = getNextSampleNumber();
      setTempSampleName(`sample_${nextNumber}`);

      // Focus the input field when it appears
      setTimeout(() => {
        if (sampleInputRef.current) {
          sampleInputRef.current.focus();
        }
      }, 100);
    }
  }, [selectedCells, showSampleInput, getNextSampleNumber]);

  // Global Enter key handler
  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      // Only handle Enter key when sample input is visible and we're not in a modal
      if (e.key === 'Enter' && showSampleInput && !showGridDialog && !showAddDialog && !showDeleteDialog) {
        // Check if we're not already in an input field (to avoid double handling)
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          handleAddSample();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyPress);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, [showSampleInput, showGridDialog, showAddDialog, showDeleteDialog, tempSampleName, selectedCells]);

  // Default Schema Functions
  const createDefault16SampleSchema = () => {
    const rows = 8;
    const cols = 12;
    const samples = [];

    let sampleNumber = 1;

    // Create samples for each row (2 samples per row, 6 cells each)
    for (let row = 0; row < rows; row++) {
      // First sample in row (columns 0-5)
      const sample1Cells = [];
      for (let col = 0; col < 6; col++) {
        sample1Cells.push({ row, col });
      }

      samples.push({
        id: Date.now() + sampleNumber,
        hue: (sampleNumber * 137.5) % 360,
        name: `sample_${sampleNumber}`,
        cells: sample1Cells,
        coordinates: `${String.fromCharCode(65 + row)}1:${String.fromCharCode(65 + row)}6`,
        classes: {}
      });
      sampleNumber++;

      // Second sample in row (columns 6-11)
      const sample2Cells = [];
      for (let col = 6; col < 12; col++) {
        sample2Cells.push({ row, col });
      }

      samples.push({
        id: Date.now() + sampleNumber,
        hue: (sampleNumber * 137.5) % 360,
        name: `sample_${sampleNumber}`,
        cells: sample2Cells,
        coordinates: `${String.fromCharCode(65 + row)}7:${String.fromCharCode(65 + row)}12`,
        classes: {}
      });
      sampleNumber++;
    }

    return {
      rows,
      cols,
      samples,
      classes: {}
    };
  };

  const handleLoadDefaultSchema = () => {
    const defaultSchema = createDefault16SampleSchema();
    onSchemaChange(defaultSchema);
    setShowBrowseModal(false);
  };

  // Schema Management Functions
  const handleUploadSchema = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const schemaData = JSON.parse(e.target.result);
        validateAndSetSchema(schemaData);
      } catch (error) {
        setUploadValidation({
          isValid: false,
          errors: ['Invalid JSON file format']
        });
        setShowUploadModal(true);
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset file input
  };

  const validateAndSetSchema = (schemaData) => {
    const errors = [];

    // Validate basic structure
    if (typeof schemaData !== 'object' || schemaData === null) {
      errors.push('Schema must be a valid JSON object');
    }

    // Validate required fields
    if (typeof schemaData.rows !== 'number' || schemaData.rows < 1 || schemaData.rows > 26) {
      errors.push('Rows must be a number between 1 and 26');
    }

    if (typeof schemaData.cols !== 'number' || schemaData.cols < 1 || schemaData.cols > 26) {
      errors.push('Columns must be a number between 1 and 26');
    }

    // Validate samples array
    if (!Array.isArray(schemaData.samples)) {
      errors.push('Samples must be an array');
    } else {
      schemaData.samples.forEach((sample, index) => {
        if (!sample.name || typeof sample.name !== 'string') {
          errors.push(`Sample ${index + 1}: Name is required and must be a string`);
        }
        if (!Array.isArray(sample.cells)) {
          errors.push(`Sample ${index + 1}: Cells must be an array`);
        } else {
          sample.cells.forEach((cell, cellIndex) => {
            if (typeof cell.row !== 'number' || cell.row < 0 || cell.row >= schemaData.rows) {
              errors.push(`Sample ${index + 1}, Cell ${cellIndex + 1}: Invalid row value`);
            }
            if (typeof cell.col !== 'number' || cell.col < 0 || cell.col >= schemaData.cols) {
              errors.push(`Sample ${index + 1}, Cell ${cellIndex + 1}: Invalid column value`);
            }
          });
        }
      });
    }

    // Validate classes structure
    if (schemaData.classes && typeof schemaData.classes !== 'object') {
      errors.push('Classes must be an object');
    }

    const isValid = errors.length === 0;
    setUploadValidation({ isValid, errors });
    setUploadedSchema(schemaData);
    setShowUploadModal(true);
  };

  const handleConfirmUpload = () => {
    if (uploadedSchema && uploadValidation.isValid) {
      // Remove id and other metadata that might conflict
      const { id, thumbnail, created_at, updated_at, user, ...cleanSchema } = uploadedSchema;

      // Ensure samples have required fields
      const processedSamples = cleanSchema.samples.map(sample => ({
        ...sample,
        id: sample.id || Date.now() + Math.random(),
        hue: sample.hue || (Math.random() * 360),
        classes: sample.classes || {}
      }));

      onSchemaChange({
        ...cleanSchema,
        samples: processedSamples,
        classes: cleanSchema.classes || {}
      });

      setShowUploadModal(false);
      setUploadedSchema(null);
      setUploadValidation({ isValid: false, errors: [] });
    }
  };

  const handleDownloadSchema = () => {
    // Create a clean schema object for download
    const downloadSchema = {
      rows: plateSchema.rows,
      cols: plateSchema.cols,
      samples: plateSchema.samples.map(sample => ({
        id: sample.id,
        name: sample.name,
        cells: sample.cells,
        coordinates: sample.coordinates,
        classes: sample.classes,
        hue: sample.hue
      })),
      classes: plateSchema.classes
    };

    const dataStr = JSON.stringify(downloadSchema, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plate_schema_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBrowseSchemes = () => {
    setShowBrowseModal(true);
  };

  // Convert cell coordinates to Excel format (A1, B2, etc.)
  const cellToExcel = useCallback((row, col) => {
    const rowLetter = String.fromCharCode(65 + row); // A, B, C, etc.
    return `${rowLetter}${col + 1}`;
  }, []);

  // Convert Excel format to cell coordinates
  const excelToCell = useCallback((excel) => {
    const match = excel.match(/^([A-Z]+)(\d+)$/);
    if (!match) return null;

    const row = match[1].charCodeAt(0) - 65;
    const col = parseInt(match[2]) - 1;

    if (row < 0 || col < 0 || row >= plateSchema.rows || col >= plateSchema.cols) {
      return null;
    }

    return { row, col };
  }, [plateSchema.rows, plateSchema.cols]);

  // Parse coordinate ranges (A1:B3, A1:A6,A8, etc.)
  const parseCoordinateRange = useCallback((coordString) => {
    if (!coordString) return [];

    const cells = [];
    const parts = coordString.split(',').map(s => s.trim());

    for (const part of parts) {
      if (part.includes(':')) {
        // Range like A1:B3
        const [start, end] = part.split(':');
        const startCell = excelToCell(start);
        const endCell = excelToCell(end);

        if (startCell && endCell) {
          const minRow = Math.min(startCell.row, endCell.row);
          const maxRow = Math.max(startCell.row, endCell.row);
          const minCol = Math.min(startCell.col, endCell.col);
          const maxCol = Math.max(startCell.col, endCell.col);

          for (let row = minRow; row <= maxRow; row++) {
            for (let col = minCol; col <= maxCol; col++) {
              cells.push({ row, col });
            }
          }
        }
      } else {
        // Single cell like A1
        const cell = excelToCell(part);
        if (cell) {
          cells.push(cell);
        }
      }
    }

    return cells;
  }, [excelToCell]);

  // Convert cells array to coordinate string
  const cellsToCoordinateString = useCallback((cells) => {
    if (!cells || cells.length === 0) return '';

    // Sort cells by row then column
    const sortedCells = [...cells].sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });

    // Try to create ranges where possible
    const ranges = [];
    let currentRange = [sortedCells[0]];

    for (let i = 1; i < sortedCells.length; i++) {
      const current = sortedCells[i];
      const last = currentRange[currentRange.length - 1];

      // Check if current cell continues the range
      const isAdjacent = (
        (current.row === last.row && current.col === last.col + 1) ||
        (current.col === currentRange[0].col && current.row === last.row + 1)
      );

      if (isAdjacent) {
        currentRange.push(current);
      } else {
        ranges.push(currentRange);
        currentRange = [current];
      }
    }
    ranges.push(currentRange);

    // Convert ranges to strings
    return ranges.map(range => {
      if (range.length === 1) {
        return cellToExcel(range[0].row, range[0].col);
      } else {
        const first = range[0];
        const last = range[range.length - 1];

        // Check if it's a proper rectangular range
        const isRectangular = range.length ===
          (last.row - first.row + 1) * (last.col - first.col + 1);

        if (isRectangular && (last.row > first.row || last.col > first.col)) {
          return `${cellToExcel(first.row, first.col)}:${cellToExcel(last.row, last.col)}`;
        } else {
          // Individual cells
          return range.map(cell => cellToExcel(cell.row, cell.col)).join(',');
        }
      }
    }).join(',');
  }, [cellToExcel]);

  // Handle cell selection
  const handleCellSelection = (cells) => {
    setSelectedCells(cells);
    if (cells.length > 0) {
      setShowSampleInput(true);
    } else {
      setShowSampleInput(false);
    }
  };

  // Select all cells on the plate
  const handleSelectAll = () => {
    const allCells = [];
    for (let row = 0; row < plateSchema.rows; row++) {
      for (let col = 0; col < plateSchema.cols; col++) {
        allCells.push({ row, col });
      }
    }
    handleCellSelection(allCells);
  };

  // Add new sample
  const handleAddSample = () => {
    if (!tempSampleName.trim() || selectedCells.length === 0) return;

    const newSample = {
      id: Date.now(),
      hue: (Date.now() * 137.5) % 360,
      name: tempSampleName.trim(),
      cells: [...selectedCells],
      coordinates: cellsToCoordinateString(selectedCells),
      classes: {}
    };

    const updatedSamples = [...plateSchema.samples, newSample];

    onSchemaChange({
      ...plateSchema,
      samples: updatedSamples
    });

    setSelectedCells([]);
    setTempSampleName('');
    setShowSampleInput(false);
  };

  // Update sample
  // In PlateSchemaStage.js, update the handleUpdateSample function to handle CLASS_DELETE:
  const handleUpdateSample = (sampleId, updates) => {
    // Check if this is a schema update (combined classes and samples)
    if (sampleId === 'SCHEMA_UPDATE') {
      console.log("Schema update received:", updates);

      // Update the entire schema with new classes and/or samples
      onSchemaChange({
        ...plateSchema,
        classes: updates.classes || plateSchema.classes,
        samples: updates.samples || plateSchema.samples
      });
      return;
    }

    // Check if this is a batch update for group renaming or class deletion
    if (sampleId === 'BATCH_UPDATE' && updates.type) {
      const { type, samplesToUpdate } = updates;

      if (type === 'GROUP_RENAME' || type === 'GROUP_DELETE' || type === 'CLASS_DELETE') {
        // Create a map of sample updates for efficient lookup
        const updateMap = {};
        samplesToUpdate.forEach(update => {
          updateMap[update.id] = update.classes;
        });

        // Update all samples at once
        const updatedSamples = plateSchema.samples.map(sample => {
          if (updateMap[sample.id]) {
            return {
              ...sample,
              classes: updateMap[sample.id]
            };
          }
          return sample;
        });

        console.log(`Batch update for ${type}`);
        console.log("Updated samples:", updatedSamples);

        onSchemaChange({
          ...plateSchema,
          samples: updatedSamples
        });
        return;
      }
    }

    // Normal single sample update
    const updatedSamples = plateSchema.samples.map(sample => {
      if (sample.id === sampleId) {
        const updatedSample = { ...sample, ...updates };

        // If coordinates changed, update cells
        if (updates.coordinates !== undefined) {
          updatedSample.cells = parseCoordinateRange(updates.coordinates);
        }

        return updatedSample;
      }
      return sample;
    });

    console.log("Update single sample");
    console.log(updatedSamples);

    onSchemaChange({
      ...plateSchema,
      samples: updatedSamples
    });
  };

  // Delete sample
  const handleDeleteSample = (sampleId) => {
    const updatedSamples = plateSchema.samples.filter(sample => sample.id !== sampleId);
    onSchemaChange({
      ...plateSchema,
      samples: updatedSamples
    });
  };

  // Handle grid size change
  const handleGridSizeChange = () => {
    if (gridRows < 1 || gridCols < 1 || gridRows > 26 || gridCols > 26) {
      setError('Grid size must be between 1x1 and 26x26');
      return;
    }

    // Filter out samples that would be outside the new grid
    const validSamples = plateSchema.samples.map(sample => ({
      ...sample,
      cells: sample.cells.filter(cell =>
        cell.row < gridRows && cell.col < gridCols
      )
    })).filter(sample => sample.cells.length > 0);

    onSchemaChange({
      ...plateSchema,
      rows: gridRows,
      cols: gridCols,
      samples: validSamples
    });

    setShowGridDialog(false);
    setError(null);
  };

  // Add row/column
  const handleAddRowColumn = (direction) => {
    let newRows = plateSchema.rows;
    let newCols = plateSchema.cols;
    let updatedSamples = [...plateSchema.samples];

    switch (direction) {
      case 'top':
        newRows++;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells.map(cell => ({ ...cell, row: cell.row + 1 }))
        }));
        break;
      case 'bottom':
        newRows++;
        break;
      case 'left':
        newCols++;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells.map(cell => ({ ...cell, col: cell.col + 1 }))
        }));
        break;
      case 'right':
        newCols++;
        break;
    }

    // Update coordinates strings
    updatedSamples = updatedSamples.map(sample => ({
      ...sample,
      coordinates: cellsToCoordinateString(sample.cells)
    }));

    onSchemaChange({
      ...plateSchema,
      rows: newRows,
      cols: newCols,
      samples: updatedSamples
    });

    setShowAddDialog(false);
  };

  // Delete row/column
  const handleDeleteRowColumn = (direction) => {
    let newRows = plateSchema.rows;
    let newCols = plateSchema.cols;
    let updatedSamples = [...plateSchema.samples];

    if ((direction === 'top' || direction === 'bottom') && newRows <= 1) return;
    if ((direction === 'left' || direction === 'right') && newCols <= 1) return;

    switch (direction) {
      case 'top':
        newRows--;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells
            .filter(cell => cell.row > 0)
            .map(cell => ({ ...cell, row: cell.row - 1 }))
        }));
        break;
      case 'bottom':
        newRows--;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells.filter(cell => cell.row < newRows)
        }));
        break;
      case 'left':
        newCols--;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells
            .filter(cell => cell.col > 0)
            .map(cell => ({ ...cell, col: cell.col - 1 }))
        }));
        break;
      case 'right':
        newCols--;
        updatedSamples = updatedSamples.map(sample => ({
          ...sample,
          cells: sample.cells.filter(cell => cell.col < newCols)
        }));
        break;
    }

    // Remove samples with no cells and update coordinates
    updatedSamples = updatedSamples
      .filter(sample => sample.cells.length > 0)
      .map(sample => ({
        ...sample,
        coordinates: cellsToCoordinateString(sample.cells)
      }));

    onSchemaChange({
      ...plateSchema,
      rows: newRows,
      cols: newCols,
      samples: updatedSamples
    });

    setShowDeleteDialog(false);
  };

  const handleSampleClassAssignment = (sampleIds, classGroup, classId) => {
    // Check if sampleIds is an array (multiple samples) or a single ID
    const idsToUpdate = Array.isArray(sampleIds) ? sampleIds : [sampleIds];

    const updatedSamples = plateSchema.samples.map(sample => {
      if (idsToUpdate.includes(sample.id)) {
        const updatedClasses = { ...sample.classes };

        if (classId === null) {
          // Remove assignment
          delete updatedClasses[classGroup];
        } else {
          // Assign class
          updatedClasses[classGroup] = classId;
        }

        return {
          ...sample,
          classes: updatedClasses
        };
      }
      return sample;
    });

    console.log("Update sample classes for", idsToUpdate.length, "samples");

    onSchemaChange({
      ...plateSchema,
      samples: updatedSamples
    });
  };

  // Handle sample highlight from table
  const handleSampleHighlight = (sampleId) => {
    setHighlightedSample(sampleId);
  };

  // Regenerate default sample name
  const handleRegenerateSampleName = () => {
    const nextNumber = getNextSampleNumber();
    setTempSampleName(`sample_${nextNumber}`);
  };

  // Handle carousel navigation
  const handleSelect = (selectedIndex) => {
    setImageIndex(selectedIndex);
  };

  return (
    <div className="plate-schema-stage">
      {/* Header with Schema Management Buttons */}
      <div className="mb-4">
        <Row className="align-items-center">
          <Col>
            <h4 className="mb-0">Define Plate Scheme</h4>
            <p className="text-muted mb-0">
              Create a layout of your chemical plate by defining sample locations and assigning them to classes.
            </p>
          </Col>
          <Col xs="auto">
            <ButtonGroup>
              <Button
                variant="outline-primary"
                onClick={handleUploadSchema}
                title="Upload schema from JSON file"
              >
                <i className="bi bi-upload me-2"></i>
                Upload Scheme
              </Button>
              <Button
                variant="outline-success"
                onClick={handleDownloadSchema}
                title="Download current schema as JSON file"
              >
                <i className="bi bi-download me-2"></i>
                Download Scheme
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleBrowseSchemes}
                title="Browse saved schemes"
              >
                <i className="bi bi-search me-2"></i>
                Browse Schemes
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      {/* Hidden file input for upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".json"
        style={{ display: 'none' }}
      />

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Row>
        <Col lg={8}>
          {/* Tools Container */}
          <Card className="mb-3">
            <Card.Body className="py-2">
              <PlateToolsContainer
                onSetGrid={() => setShowGridDialog(true)}
                onAddRowColumn={() => setShowAddDialog(true)}
                onDeleteRowColumn={() => setShowDeleteDialog(true)}
                onSelectAll={handleSelectAll}
                gridSize={`${plateSchema.rows}×${plateSchema.cols}`}
                samplesCount={plateSchema.samples.length}
                selectedCellsCount={selectedCells.length}
              />
            </Card.Body>
          </Card>

          {/* Sample Input Bar */}
          {showSampleInput && (
            <Card className="mb-3 border-primary">
              <Card.Body className="py-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">
                    Selected: {selectedCells.length} cell(s)
                  </span>
                  <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                    <Form.Control
                      ref={sampleInputRef}
                      type="text"
                      placeholder="Enter sample name..."
                      value={tempSampleName}
                      onChange={(e) => setTempSampleName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSample()}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={handleRegenerateSampleName}
                      title="Generate next sample number"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleAddSample}
                      disabled={!tempSampleName.trim()}
                      title="Add sample (Enter)"
                    >
                      <i className="bi bi-check"></i>
                    </Button>
                  </InputGroup>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedCells([]);
                      setShowSampleInput(false);
                    }}
                    title="Cancel selection"
                  >
                    <i className="bi bi-x"></i>
                  </Button>
                </div>
                <div className="mt-1">
                  <small className="text-muted">
                    Default naming: sample_1, sample_2, etc. | Press <kbd>Enter</kbd> to confirm
                  </small>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Plate Grid */}
          <Card>
            <Card.Body>
              <PlateGrid
                rows={plateSchema.rows}
                cols={plateSchema.cols}
                samples={plateSchema.samples}
                selectedCells={selectedCells}
                highlightedSample={highlightedSample}
                onCellSelection={handleCellSelection}
                onSelectionStart={() => setIsSelecting(true)}
                onSelectionEnd={() => setIsSelecting(false)}
                selectedClassGroup={selectedClassGroup}
                classes={plateSchema.classes}
                onSampleClassAssignment={handleSampleClassAssignment}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Images Carousel */}
          {selectedJobs.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">
                  Images ({selectedJobs.length})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <Carousel
                  activeIndex={imageIndex}
                  onSelect={handleSelect}
                  indicators={false}
                  controls={selectedJobs.length > 1}
                  interval={null}
                  className="images-carousel-clean"
                >
                  {selectedJobs.map((job, index) => (
                    <Carousel.Item key={job.id}>
                      <ImageCarouselItem
                        job={job}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </Card.Body>
            </Card>
          )}

          {/* Sample Table */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                Samples ({plateSchema.samples.length})
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              <SampleTable
                samples={plateSchema.samples}
                onUpdateSample={handleUpdateSample}
                onDeleteSample={handleDeleteSample}
                onHighlightSample={handleSampleHighlight}
                highlightedSample={highlightedSample}
              />
            </Card.Body>
          </Card>

          {/* Classification Manager */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">Sample Classification</h6>
            </Card.Header>
            <Card.Body>
              <ClassificationManager
                samples={plateSchema.samples}
                classes={plateSchema.classes}
                onUpdateSample={handleUpdateSample}
                onUpdateClasses={(classes) => onSchemaChange({ ...plateSchema, classes })}
                selectedClassGroup={selectedClassGroup}
                onSelectClassGroup={setSelectedClassGroup}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Browse Schemes Modal */}
      <Modal show={showBrowseModal} onHide={() => setShowBrowseModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Browse Plate Schemes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Default Schema Button */}
          <div className="mb-3">
            <Button
              variant="outline-primary"
              onClick={handleLoadDefaultSchema}
              title="Set default 8×12 plate with 16 samples (2 groups of 6 cells per row)"
              className="w-100"
            >
              <i className="bi bi-stars me-2"></i>
              Set Default 16 Samples Plate
              <br />
              <small className="text-muted">8×12 grid with 2 sample groups per row (6 cells each)</small>
            </Button>
          </div>

          <Form.Control
            type="text"
            placeholder="Search schemes by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {/* Placeholder for future PlateSchemaCard components */}
            <div className="text-center text-muted py-4">
              <i className="bi bi-inbox fs-1 mb-2 d-block"></i>
              <p>No saved schemes found</p>
              <small>Saved schemes will appear here when backend integration is implemented</small>
            </div>

            {/* Placeholder for Load More Button */}
            {/* {hasMorePages && (
              <div className="text-center mt-3">
                <Button
                  variant="outline-secondary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  size="sm"
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )} */}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBrowseModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Schema Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Plate Schema</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadValidation.isValid && uploadedSchema ? (
            <div>
              <Alert variant="success" className="mb-3">
                <i className="bi bi-check-circle-fill me-2"></i>
                Schema validation successful!
              </Alert>

              <div className="schema-preview">
                <h6>Schema Preview:</h6>
                <div className="row small text-muted mb-3">
                  <div className="col">
                    <strong>Grid Size:</strong> {uploadedSchema.rows}×{uploadedSchema.cols}
                  </div>
                  <div className="col">
                    <strong>Samples:</strong> {uploadedSchema.samples?.length || 0}
                  </div>
                  <div className="col">
                    <strong>Classes:</strong> {uploadedSchema.classes ? Object.keys(uploadedSchema.classes).length : 0}
                  </div>
                </div>

                {uploadedSchema.samples && uploadedSchema.samples.length > 0 && (
                  <div className="mt-3">
                    <h6>Sample Summary:</h6>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Cells</th>
                            <th>Coordinates</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadedSchema.samples.slice(0, 10).map((sample, index) => (
                            <tr key={index}>
                              <td>{sample.name}</td>
                              <td>{sample.cells?.length || 0}</td>
                              <td className="text-muted">{sample.coordinates || 'N/A'}</td>
                            </tr>
                          ))}
                          {uploadedSchema.samples.length > 10 && (
                            <tr>
                              <td colSpan="3" className="text-center text-muted">
                                ... and {uploadedSchema.samples.length - 10} more samples
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Schema validation failed!
              </Alert>

              <div className="validation-errors">
                <h6>Validation Errors:</h6>
                <ul className="text-danger">
                  {uploadValidation.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>

              <Alert variant="info" className="small">
                <strong>Expected format:</strong> JSON file with rows, cols, samples array, and optional classes object.
                Samples should have name, cells array, and optional coordinates.
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          {uploadValidation.isValid && (
            <Button variant="primary" onClick={handleConfirmUpload}>
              Confirm & Load Schema
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Grid Size Dialog */}
      <Modal show={showGridDialog} onHide={() => setShowGridDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Grid Size</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Rows</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="26"
                    value={gridRows}
                    onChange={(e) => setGridRows(parseInt(e.target.value) || 1)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Columns</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="26"
                    value={gridCols}
                    onChange={(e) => setGridCols(parseInt(e.target.value) || 1)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="mt-3 text-muted small">
              Current: {plateSchema.rows}×{plateSchema.cols} |
              New: {gridRows}×{gridCols}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGridDialog(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGridSizeChange}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Row/Column Dialog */}
      <Modal show={showAddDialog} onHide={() => setShowAddDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Row or Column</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            <Button variant="outline-primary" onClick={() => handleAddRowColumn('top')}>
              <i className="bi bi-arrow-up me-2"></i>Add Row at Top
            </Button>
            <Button variant="outline-primary" onClick={() => handleAddRowColumn('bottom')}>
              <i className="bi bi-arrow-down me-2"></i>Add Row at Bottom
            </Button>
            <Button variant="outline-primary" onClick={() => handleAddRowColumn('left')}>
              <i className="bi bi-arrow-left me-2"></i>Add Column at Left
            </Button>
            <Button variant="outline-primary" onClick={() => handleAddRowColumn('right')}>
              <i className="bi bi-arrow-right me-2"></i>Add Column at Right
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Row/Column Dialog */}
      <Modal show={showDeleteDialog} onHide={() => setShowDeleteDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Row or Column</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            <Button
              variant="outline-danger"
              onClick={() => handleDeleteRowColumn('top')}
              disabled={plateSchema.rows <= 1}
            >
              <i className="bi bi-arrow-up me-2"></i>Delete Top Row
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => handleDeleteRowColumn('bottom')}
              disabled={plateSchema.rows <= 1}
            >
              <i className="bi bi-arrow-down me-2"></i>Delete Bottom Row
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => handleDeleteRowColumn('left')}
              disabled={plateSchema.cols <= 1}
            >
              <i className="bi bi-arrow-left me-2"></i>Delete Left Column
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => handleDeleteRowColumn('right')}
              disabled={plateSchema.cols <= 1}
            >
              <i className="bi bi-arrow-right me-2"></i>Delete Right Column
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Image Carousel Item Component - Clean version
const ImageCarouselItem = ({ job }) => {
  // Use rotated_image if available, otherwise use thumbnail
  const imagePath = job?.rotated_image || job.thumbnail;
  const [image, imageStatus, imageError] = useImageWithAuth('photo_processing', imagePath);

  return (
    <div className="image-carousel-item-clean">
      <div className="carousel-image-container-clean">
        {imageStatus === 'loading' && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading image...</span>
              </div>
              <p className="text-muted small">Loading image...</p>
            </div>
          </div>
        )}

        {imageStatus === 'loaded' && image && (
          <img
            src={image.src}
            alt={`Job ${job.id}`}
            className="carousel-image-clean"
          />
        )}

        {imageStatus === 'failed' && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center text-danger">
              <i className="bi bi-image-fill fs-1 mb-2"></i>
              <p className="small">Failed to load image</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlateSchemaStage;